
import React, { useState, useEffect } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AuthContext } from './AuthContext';
import { cacheSession, getCachedSession, isGuestUser } from './utils';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);

  useEffect(() => {
    // Check for session in localStorage (offline caching)
    const cachedSession = getCachedSession();
    if (cachedSession) {
      setSession(cachedSession);
      setUser(cachedSession.user);
      setIsGuest(isGuestUser(cachedSession));
    }

    // Get current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsGuest(isGuestUser(session));
      
      // Cache session for offline use
      cacheSession(session);
      
      setIsLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsGuest(isGuestUser(session));
      
      // Update cached session
      cacheSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const convertGuestToFull = () => {
    // This will redirect to the auth page to upgrade from guest to full account
    window.location.href = '/auth?upgrade=true';
  };

  return (
    <AuthContext.Provider value={{ session, user, isLoading, isGuest, signOut, convertGuestToFull }}>
      {children}
    </AuthContext.Provider>
  );
};
